/**
 * BookOverlay — slide-in overlay that shows BookView on top of StoryBuilder.
 * Manages slide animation, voice deactivation in creating mode, and Esc to close.
 */

import React, { useEffect, useRef, useState } from "react";
import { Animated, Platform, StyleSheet, useWindowDimensions } from "react-native";
import { useSocketStore } from "../store/socketStore";
import { useComicStore } from "../store/comicStore";
import { getGenPhase, selectDrawingPageDone } from "../store/comicGenStatus";
import { useStoryStore } from "../store/storyStore";
import BookView from "./book/BookView";

interface BookOverlayProps {
  visible: boolean;
  onClose: () => void;
}

export default function BookOverlay({ visible, onClose }: BookOverlayProps) {
  const { width } = useWindowDimensions();
  const [mounted, setMounted] = useState(visible);
  const slideAnim = useRef(new Animated.Value(visible ? 0 : 1)).current;
  const isAnimatingRef = useRef(false);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      isAnimatingRef.current = true;
      Animated.timing(slideAnim, { toValue: 0, duration: 300, useNativeDriver: true }).start(({ finished }) => {
        if (finished) isAnimatingRef.current = false;
      });
    } else if (mounted) {
      isAnimatingRef.current = true;
      Animated.timing(slideAnim, { toValue: 1, duration: 300, useNativeDriver: true }).start(({ finished }) => {
        if (!finished) return;
        isAnimatingRef.current = false;
        setMounted(false);
      });
    }
  }, [visible]);

  useEffect(() => {
    if (Platform.OS !== "web" || !visible) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [visible, onClose]);

  if (!mounted) return null;

  return (
    <Animated.View style={[styles.overlay, { transform: [{ translateX: slideAnim.interpolate({ inputRange: [0, 1], outputRange: [0, width] }) }] }]}>
      <StoryBookTab onBack={onClose} />
    </Animated.View>
  );
}

function StoryBookTab({ onBack }: { onBack: () => void }) {
  const { pages, genState, currentPage, currentDrawingPage, layoutComplete, totalPages, setCurrentPage } = useComicStore();
  const storyTitle = useStoryStore((s) => s.title);
  const { generateComic } = useSocketStore();
  const isGenerating = genState === "generating_prompts" || genState === "generating_pages";
  const saveState = useComicStore((s) => s.saveState);
  const [showSaveStatus, setShowSaveStatus] = useState(false);
  const prevSaveStateRef = useRef(saveState);

  useEffect(() => {
    if (saveState === prevSaveStateRef.current) return;
    prevSaveStateRef.current = saveState;
    if (saveState === "saved" || saveState === "failed") {
      setShowSaveStatus(true);
      const timer = setTimeout(() => setShowSaveStatus(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [saveState]);

  const drawingPageDone = selectDrawingPageDone({ pages, currentDrawingPage });
  const phase = getGenPhase({ genState, currentDrawingPage, drawingPageDone, layoutComplete, totalPages });
  let genStatus: string | undefined;
  switch (phase?.kind) {
    case "creatingLayout": genStatus = "Creating story layout..."; break;
    case "drawingCover": genStatus = "Drawing cover..."; break;
    case "awaitingPages": genStatus = "Getting the next pages ready..."; break;
    case "drawingPage":
      genStatus = phase.total != null
        ? `Drawing page ${phase.page} of ${phase.total}...`
        : `Drawing page ${phase.page}...`;
      break;
  }

  const statusText = isGenerating
    ? genStatus
    : showSaveStatus && saveState === "saved"
      ? "Saved to your collection"
      : showSaveStatus && saveState === "failed"
        ? "Failed to save"
        : undefined;

  return (
    <BookView
      pages={pages}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      title={storyTitle || undefined}
      onBack={onBack}
      onRedraw={generateComic}
      actionsDisabled={isGenerating || pages.length === 0}
      statusText={statusText}
      voiceOver={genState === "done"}
      totalKnown={!isGenerating || layoutComplete}
      totalPages={totalPages}
    />
  );
}

const styles = StyleSheet.create({
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
});
